import { Box, Center, IconButton, Image, Spinner, useBoolean } from '@chakra-ui/react';
import { UPGRADE_REDIRECT_BIZ_LINK, UPGRADE_REDIRECT_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, ICON_CREATED, UPGRADE } from 'constants/mixpanel';
import { useIconData } from 'hooks/useIcon';
import { useIcons8Thumbnail } from 'hooks/useIcons8Thumbnail';
import { IconHit } from 'hooks/usePaginatedIconSearch';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { PermissionBadge, UserType, UserTypes, hasResourcePermission } from 'modules/User';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { WidgetType } from 'types/widget.types';
import { capitalizeStr } from 'utils/capitalizeStr';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { computeIconViewBox } from 'widgets/IconWidget/IconWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { ICON_PREVIEW_SIZE } from './IconWidgetMenu.config';

type OnClickIconWidget = (id: string, viewBox: string) => void;

type OnCreateWidgetData = (id: string, viewBox: string) => NewWidget | null;

interface IconPreviewProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  onClickIconWidget: OnClickIconWidget;
  onCreateWidgetData: OnCreateWidgetData;
  onLoad: () => void;
  onLoadError: () => void;
  onOpenModal: () => void;
  isMono?: boolean;
  iconPath?: string;
  iconThumbnailUrl?: string;
  setUpgradeCallback?: (callback: () => void, upgradePlan: UserType) => void;
  disableDragDrop?: boolean;
}

type PreviewThumbnailProps = {
  id: string;
  thumbnailUrl: string;
  onLoad?: () => void;
};
const PreviewThumbnail = ({ id, thumbnailUrl, onLoad }: PreviewThumbnailProps) => (
  // need to set pointer events to none here as drag event is triggered for image / svg elements within the draggable container
  <Image src={thumbnailUrl} alt={id} w='full' h='full' objectFit='contain' onLoad={onLoad} pointerEvents='none' />
);

/**
 * ICON PREVIEW COMPONENT
 *
 * Component to render icon preview in the left icon widget menu
 */
export const IconPreview = ({
  id,
  userType,
  permission,
  onClickIconWidget,
  onCreateWidgetData,
  onLoad,
  onLoadError,
  isMono,
  iconPath,
  iconThumbnailUrl,
  setUpgradeCallback,
  onOpenModal,
  disableDragDrop,
}: IconPreviewProps) => {
  // Icons8 Icon
  if (iconPath) {
    return (
      <Icons8Icon
        id={id}
        isMono={!!isMono}
        userType={userType}
        permission={permission}
        onClickIconWidget={onClickIconWidget}
        onCreateWidgetData={onCreateWidgetData}
        onLoad={onLoad}
        onLoadError={onLoadError}
        iconPath={iconPath}
        disableDragDrop={disableDragDrop}
      />
    );
  }

  // Icons8 Illustration
  if (iconThumbnailUrl) {
    return (
      <Icons8Illustration
        id={id}
        userType={userType}
        permission={permission}
        onClickIconWidget={onClickIconWidget}
        onCreateWidgetData={onCreateWidgetData}
        onLoad={onLoad}
        onLoadError={onLoadError}
        thumbnailUrl={iconThumbnailUrl}
        disableDragDrop={disableDragDrop}
      />
    );
  }

  // S3 icon
  return (
    <S3Icon
      id={id}
      userType={userType}
      permission={permission}
      onClickIconWidget={onClickIconWidget}
      onCreateWidgetData={onCreateWidgetData}
      onLoad={onLoad}
      setUpgradeCallback={setUpgradeCallback}
      onOpenModal={onOpenModal}
      disableDragDrop={disableDragDrop}
    />
  );
};

interface Icons8IconProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  iconPath: string;
  isMono: boolean;
  disableDragDrop?: boolean;
  onClickIconWidget: OnClickIconWidget;
  onCreateWidgetData: OnCreateWidgetData;
  onLoad: () => void;
  onLoadError: () => void;
}

/**
 * ICONS8 ICON PREVIEW COMPONENT
 *
 * Render icons8 preview using the icons8 img url
 */
const Icons8Icon = ({
  id,
  userType,
  permission,
  disableDragDrop,
  onLoadError,
  onLoad,
  onClickIconWidget,
  onCreateWidgetData,
}: Icons8IconProps): ReactElement | null => {
  const { data: thumbnailBlob } = useIcons8Thumbnail(id);
  const [isLoading, setIsLoading] = useBoolean(false);

  if (!thumbnailBlob) {
    return null;
  }

  const thumbnailUrl = URL.createObjectURL(thumbnailBlob);

  const onClickIcon = async () => {
    const viewBox = (await computeIconViewBox(id, false, setIsLoading)) as string;
    if (!viewBox) return onLoadError();
    onClickIconWidget(id, viewBox);
  };

  const createWidgetData = () => {
    const widgetData = onCreateWidgetData(id, '') as NewWidget;
    return { ...widgetData, ...{ iconId: id, shouldCalculateViewBox: true } };
  };

  return (
    <WidgetDragDropWrapper
      isDraggable={!disableDragDrop}
      widgetData={createWidgetData()}
      type={WidgetType.Icon}
      dragImage={<PreviewThumbnail thumbnailUrl={thumbnailUrl} id={id} />}
      trackingEventData={{
        event_name: ICON_CREATED.eventName,
        properties: {
          from: ICON_CREATED.from.Search,
          icon_id: id,
        },
      }}
    >
      <IconThumbnail
        icon={<PreviewThumbnail thumbnailUrl={thumbnailUrl} id={id} onLoad={onLoad} />}
        onClick={onClickIcon}
        userType={userType}
        permission={permission}
        isLoading={isLoading}
      />
    </WidgetDragDropWrapper>
  );
};

interface Icons8IllustrationProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  thumbnailUrl: string;
  disableDragDrop?: boolean;
  onClickIconWidget: OnClickIconWidget;
  onCreateWidgetData: OnCreateWidgetData;
  onLoad: () => void;
  onLoadError: () => void;
}

/**
 * ICONS8 ILLUSTRATION PREVIEW COMPONENT
 *
 * Render icons8 preview using the icons8 img url provided from search endpoint
 */
const Icons8Illustration = ({
  id,
  userType,
  permission,
  thumbnailUrl,
  disableDragDrop,
  onLoadError,
  onClickIconWidget,
  onCreateWidgetData,
  onLoad,
}: Icons8IllustrationProps): ReactElement | null => {
  const [isLoading, setIsLoading] = useBoolean(false);

  const onClickIcon = async () => {
    const viewBox = (await computeIconViewBox(id, true, setIsLoading)) as string;
    if (!viewBox) return onLoadError();

    onClickIconWidget(id, viewBox);
  };

  const createWidgetData = () => {
    const widgetData = onCreateWidgetData(id, '') as NewWidget;
    return { ...widgetData, ...{ iconId: id, shouldCalculateViewBox: true, isIllustration: true } };
  };

  return (
    <WidgetDragDropWrapper
      isDraggable={!disableDragDrop}
      widgetData={createWidgetData()}
      type={WidgetType.Icon}
      dragImage={<PreviewThumbnail thumbnailUrl={thumbnailUrl} id={id} />}
      trackingEventData={{
        event_name: ICON_CREATED.eventName,
        properties: {
          from: ICON_CREATED.from.Search,
          icon_id: id,
        },
      }}
    >
      <IconThumbnail
        icon={<PreviewThumbnail thumbnailUrl={thumbnailUrl} id={id} onLoad={onLoad} />}
        onClick={onClickIcon}
        userType={userType}
        permission={permission}
        isLoading={isLoading}
      />
    </WidgetDragDropWrapper>
  );
};

const iconStyle = { width: '100%', height: '100%' };

interface S3IconProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  onClickIconWidget: OnClickIconWidget;
  onCreateWidgetData: OnCreateWidgetData;
  onLoad: () => void;
  onOpenModal: () => void;
  setUpgradeCallback?: (callback: () => void, upgradePlan: UserType) => void;
  disableDragDrop?: boolean;
}

/**
 * S3 ICON PREVIEW COMPONENT
 *
 * Renders icons from S3 bucket - these icons are pulled from Algolia search
 */
const S3Icon = ({
  id,
  userType,
  permission,
  onClickIconWidget,
  onCreateWidgetData,
  onLoad,
  setUpgradeCallback,
  onOpenModal,
  disableDragDrop,
}: S3IconProps): ReactElement | null => {
  const iconData = useIconData({ iconId: id });
  const { trackChameleon } = useChameleon();

  // The default onGated behaviour, redirects to upgrade page
  const onGated = () => {
    const userPlanStr = userType === 'default' ? 'Free' : userType;
    const iconPermissionStr = permission === 'default' ? 'Free' : permission;
    const mpFeatureStr = `${capitalizeStr(iconPermissionStr)} Icons`;

    Mixpanel.track(UPGRADE.eventName, {
      Feature: mpFeatureStr,
      'User Plan': capitalizeStr(userPlanStr),
    });
    trackChameleon(UPGRADE.eventName);

    setAnalyticsUpgradeFeatureProp(mpFeatureStr);
    const link = permission === UserTypes.Business ? UPGRADE_REDIRECT_BIZ_LINK : UPGRADE_REDIRECT_LINK;

    window.open(link, '_self');
  };

  if (!iconData) return null;

  const { svg, description, viewBox = '' } = iconData;
  const title = 'icon';

  const allowAccess = hasResourcePermission(userType, permission);

  const onClickIcon = () => {
    if (allowAccess) {
      onClickIconWidget(id, viewBox);
    } else {
      Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
        Feature: `${capitalizeStr(permission)} Icons`,
      });
      setUpgradeCallback?.(onGated, permission);
      onOpenModal();
    }
  };

  const createWidgetData = () => onCreateWidgetData(id, viewBox) as NewWidget;

  return (
    <WidgetDragDropWrapper
      isDraggable={allowAccess && !disableDragDrop}
      widgetData={createWidgetData()}
      type={WidgetType.Icon}
      dragImage={<SVG style={iconStyle} src={svg} />}
      trackingEventData={{
        event_name: ICON_CREATED.eventName,
        properties: {
          from: ICON_CREATED.from.Search,
          icon_id: id,
        },
      }}
    >
      <IconThumbnail
        icon={
          <SVG
            uniquifyIDs={true}
            title={title}
            description={description}
            onLoad={onLoad}
            role='img'
            style={iconStyle}
            src={svg}
            // need to set pointer events to none here as drag event is triggered for image / svg elements within the draggable container
            pointerEvents='none'
          />
        }
        onClick={onClickIcon}
        userType={userType}
        permission={permission}
      />
    </WidgetDragDropWrapper>
  );
};

interface IconThumbnailProps {
  icon: ReactElement;
  onClick: () => void;
  buttonAriaLabel?: string;
  userType: UserType;
  permission: IconHit['type'];
  isLoading?: boolean;
}

/**
 * THUMBNAIL COMPONENT
 *
 * Renders icon thumbnail with permission badge
 */
const IconThumbnail = ({
  icon,
  onClick,
  buttonAriaLabel,
  userType,
  permission,
  isLoading,
}: IconThumbnailProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.iconsMenu' });
  const showBadge = !hasResourcePermission(userType, permission);
  const sizePx = ICON_PREVIEW_SIZE + 'px';

  const ariaLabel = buttonAriaLabel || t('iconPreviewLabel');

  return (
    <Box position={'relative'} role='group'>
      <IconButton
        width={sizePx}
        height={sizePx}
        p={2}
        aria-label={ariaLabel}
        onClick={onClick}
        variant={'icon-btn-side-menu'}
        icon={icon}
      />
      {showBadge && (
        <PermissionBadge
          permission={permission}
          position={'absolute'}
          top={0}
          left={'50%'}
          transform={'translate(-50%, 0)'}
        />
      )}
      {isLoading && (
        <Center position='absolute' borderRadius='base' bg='black-alpha.500' w={sizePx} h={sizePx} top={0} left={0}>
          <Spinner size='md' color='brand.500' />
        </Center>
      )}
    </Box>
  );
};
